import { create } from "zustand";
import { MultiGetResponse } from "../../Contracts/Common";
import { useRequestStore } from "../BackendStore";
import { RefundPayload, Transaction } from "../../Contracts/Transaction";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const transactionUrl = "/v1/payments/transactions";
const refundUrl = "/v1/payments/transaction/{transaction_id}/refund";
const getRefundDetailsUrl = "/v1/payments/transaction/{transaction_id}/refund/details";

// const getRefundReciept = "/v1/payments/transaction/{transaction_id}/refund/receipt"; TODO: @Shivank need to implement this endpoint

interface TransactionState {
  isLoading: boolean;
  transactions: Transaction[];
  transactionsCount?: number;
  resetTransactions: () => void;
  getTransactions: (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    q?: string
  ) => Promise<void>;
  doRefund: (
    getAccessTokenSilently: any,
    transactionId: number,
    refundReason: RefundPayload
  ) => Promise<string>;
  refundDetails?: RefundDetails;
  getRefundDetails: (getAccessTokenSilently: any, transactionId: number) => Promise<void>;
}

export interface RefundDetails {
  allow_refund: boolean;
  available_balance_after_refund: number;
  current_balance: number;
  estimated_settlement_date: string;
  message: string;
  payment_method: string;
  refund_amount: number;
}

export const useTransactionStore = create<TransactionState>(set => {
  const { getRequest, postRequest } = useRequestStore.getState();
  const { addBanner } = useBannerStore.getState();

  const getTransactions = async (
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string,
    q?: string
  ) => {
    set({ isLoading: true });
    try {
      const response = await getRequest<MultiGetResponse<Transaction>>(
        getAccessTokenSilently,
        transactionUrl,
        { limit, sort, page, include_total_count: true, filters, q }
      );
      set({
        transactions: response.data,
        transactionsCount: response.filtered_count_map?.all,
        isLoading: false
      });
    } catch (error) {
      console.error("Error fetching transactions", error);
      set({ isLoading: false });
    }
  };

  const resetTransactions = () => {
    set({
      transactions: [],
      transactionsCount: undefined
    });
  };

  const doRefund = async (
    getAccessTokenSilently: any,
    transactionId: number,
    refundPayload: RefundPayload
  ) => {
    try {
      const response = await postRequest<RefundPayload, { message: string }>(
        getAccessTokenSilently,
        refundUrl.replace("{transaction_id}", transactionId.toString()),
        refundPayload,
        {}
      );
      addBanner({
        type: BannerType.Success,
        message: response.message
      });
      return response.message;
    } catch (error) {
      console.error("Error refunding transaction", error);
      set({ isLoading: false });
      return "Error refunding transaction";
    }
  };

  const getRefundDetails = async (getAccessTokenSilently: any, transactionId: number) => {
    try {
      const response = await getRequest<RefundDetails>(
        getAccessTokenSilently,
        getRefundDetailsUrl.replace("{transaction_id}", transactionId.toString())
      );
      set({ refundDetails: response });
    } catch (error) {
      addBanner({
        type: BannerType.Error,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Refund failed. Please try again later."
      });
      throw error;
    }
  };

  return {
    transactions: [],
    getTransactions,
    resetTransactions,
    isLoading: true,
    doRefund,
    getRefundDetails,
    refundDetails: undefined
  };
});
